/*
 * Central timezone helper
 * ---
 *
 * Single source of truth for reading and applying the active brand timezone.
 *
 * Writing:
 *   BackOfficeContext.ChangeTimezone(value) → also writes window.__BO_TZ__.
 *
 * Reading:
 *   - Inside React components:  useTimezone() hook (re-renders on change).
 *   - Inside helpers / utils:   getActiveTimezone() (synchronous cache read).
 *
 * Both paths return the same value because ChangeTimezone updates both.
 */

import dayjs, { Dayjs } from "dayjs";

/**
 * Global synchronous cache. Written by BackOfficeContext.ChangeTimezone.
 * Helper/util functions that cannot call React hooks read from here.
 */
declare global {
  interface Window {
    __BO_TZ__?: string;
  }
}

export const BO_TZ_WINDOW_KEY = "__BO_TZ__" as const;

/**
 * Write the active timezone to the synchronous cache.
 * Called exclusively from BackOfficeContext.ChangeTimezone.
 */
export const setActiveTimezoneCache = (tz: string | undefined) => {
  if (typeof window === "undefined") return;
  if (tz && isValidTimezone(tz)) {
    window.__BO_TZ__ = tz;
  }
};

/**
 * Read the active timezone synchronously.
 * Fallback order: window cache → browser guess → UTC.
 */
export const getActiveTimezone = (): string => {

  if (typeof window !== "undefined" && window.__BO_TZ__) {
    return window.__BO_TZ__;
  }

  try {
    const guess = dayjs.tz.guess();
    if (guess) return guess;
  } catch {
    // fall through
  }

  return "UTC";

};

/**
 * Whether a synchronous active timezone is available in the cache.
 * Used by gates (e.g. CoreDataTable) to avoid firing API filters before
 * the login response populated the timezone.
 */
export const isTimezoneReady = (): boolean => {
  return typeof window !== "undefined" && typeof window.__BO_TZ__ === "string" && window.__BO_TZ__.length > 0;
};

/**
 * Validate an IANA timezone string by attempting to use it with Intl.
 */
export const isValidTimezone = (tz: string): boolean => {
  if (!tz) return false;
  try {
    new Intl.DateTimeFormat(undefined, { timeZone: tz });
    return true;
  } catch {
    return false;
  }
};

/**
 * Parse an incoming value and return a Dayjs anchored in the active timezone.
 * - Strings with timezone info (e.g. "2026-01-15T07:30:00Z") keep their absolute
 *   instant and display in active tz.
 * - Naive strings (e.g. "2026-01-15") are interpreted as active tz local time.
 */
export const toUserTz = (input: Dayjs | Date | string | number): Dayjs => {
  const tz = getActiveTimezone();
  if (input === null || input === undefined) return dayjs.tz(undefined, tz);

  // Dayjs instances carry their own tz/offset; just convert display zone.
  if (dayjs.isDayjs(input)) {
    return input.tz(tz);
  }

  if (typeof input === "string") {
    // Has timezone info → keep absolute instant, convert display zone.
    if (/Z|[+-]\d{2}:?\d{2}$/.test(input)) {
      return dayjs.utc(input).tz(tz);
    }
    // Naive string → parse in active tz.
    return dayjs.tz(input, tz);
  }

  return dayjs(input).tz(tz);
};

/**
 * Format a value for display in the active timezone.
 * Prefer calling this (or the format helpers that wrap it) over raw dayjs().format().
 */
export const formatInUserTz = (
  input  : Dayjs | Date | string | number,
  pattern: string,
  locale?: string
): string => {
  const d = toUserTz(input);
  return locale ? d.locale(locale).format(pattern) : d.format(pattern);
};

/**
 * "Start of day" in active timezone → UTC ISO string.
 * Use when sending filter ranges to the backend (backend expects UTC).
 */
export const startOfDayUtc = (
  localDate: Dayjs | Date | string | number,
  tz        : string = getActiveTimezone()
): string => {
  return toUserTzWith(tz, localDate).startOf("day").toISOString();
};

/**
 * "End of day" in active timezone → UTC ISO string.
 */
export const endOfDayUtc = (
  localDate: Dayjs | Date | string | number,
  tz        : string = getActiveTimezone()
): string => {
  return toUserTzWith(tz, localDate).endOf("day").toISOString();
};

/**
 * Convert a DatePicker (dayjs in browser-local) output to a UTC ISO string,
 * interpreting the picker's wall-clock values as active-tz local time.
 */
export const localDateToUtcIso = (
  value: Dayjs | Date | string | number,
  tz    : string = getActiveTimezone()
): string => {
  // If value already carries a zone (ISO with Z or offset), trust it.
  if (typeof value === "string" && /Z|[+-]\d{2}:?\d{2}$/.test(value)) {
    return dayjs.utc(value).toISOString();
  }

  // Treat wall-clock parts as "this is what the user typed in active tz".
  const wall = dayjs(value);
  const rebuilt = dayjs.tz(wall.format("YYYY-MM-DDTHH:mm:ss.SSS"), tz);
  return rebuilt.toISOString();
};

/**
 * Internal: tz-aware variant of toUserTz that accepts an explicit tz.
 */
const toUserTzWith = (tz: string, input: Dayjs | Date | string | number): Dayjs => {
  if (dayjs.isDayjs(input)) return input.tz(tz);
  if (typeof input === "string") {
    if (/Z|[+-]\d{2}:?\d{2}$/.test(input)) return dayjs.utc(input).tz(tz);
    return dayjs.tz(input, tz);
  }
  return dayjs(input).tz(tz);
};
